import DOMPurify from 'dompurify';

export interface Message {
  messageId: number | null;
  subject: string | null;
  body: string | null;
  storeId: string | null;
  vendorId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  messageRecipientId: number | null;
  purposeId: number | null;
  saRole: string | null;
  vendorAckBy: string | null;
  vendorAckAt: string | null;
  saAckBy: string | null;
  saAckAt: string | null;
  purposeName: string | null;
}

// Type for safe message handling - the entire message could be null
export type SafeMessage = Message | null;

/**
 * Configure DOMPurify to allow safe HTML tags for message content
 * This configuration is specifically tailored for system-generated messages
 */
const ALLOWED_TAGS = [
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'br',
  'p',
  'div',
  'span',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li'
];

const ALLOWED_ATTRIBUTES = [
  'border',
  'cellpadding',
  'cellspacing'
  // Note: We explicitly exclude 'style' and 'class' for better security
  // and to prevent CSS injection attacks
];

/**
 * Strip HTML tags from text content
 * @param html - HTML string to strip tags from
 * @returns Plain text without HTML tags
 */
function stripHtmlTags(html: string): string {
  if (!html) return '';

  // Remove HTML tags using regex
  return html
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
    .trim();
}

/**
 * Gets the sanitized message content safe for HTML rendering
 *
 * SECURITY: This function uses DOMPurify to sanitize HTML content and prevent XSS attacks.
 * It follows a whitelist approach, only allowing specific safe HTML tags and attributes.
 *
 * Allowed elements: tables, basic formatting, headings, lists
 * Blocked elements: scripts, iframes, style attributes, event handlers
 *
 * @param message - The message object containing HTML content
 * @returns Sanitized HTML content safe for dangerouslySetInnerHTML
 */
export function getMessageContent(message: string): string {
  if (!message) return 'No content available';

  // Sanitize HTML content to prevent XSS attacks while preserving safe formatting
  return sanitizeHtmlContent(message);
}

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting
 * Uses DOMPurify with a restrictive whitelist approach for maximum security
 * @param html - Raw HTML content to sanitize
 * @returns Sanitized HTML safe for rendering
 */
export function sanitizeHtmlContent(html: string): string {
  if (!html) return '';

  try {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR: ALLOWED_ATTRIBUTES,
      KEEP_CONTENT: true, // Keep text content even if tags are removed
      RETURN_DOM_FRAGMENT: false,
      RETURN_DOM: false,
      // Additional security options
      FORBID_ATTR: ['style', 'on*'], // Block style attributes and event handlers
      FORBID_TAGS: ['script', 'object', 'embed', 'iframe'], // Explicitly forbid dangerous tags
      WHOLE_DOCUMENT: false, // Only sanitize fragments, not full documents
      SANITIZE_DOM: true // Enable additional DOM sanitization
    });
  } catch (error) {
    // Fallback to plain text if sanitization fails
    console.warn('HTML sanitization failed, falling back to plain text:', error);
    return stripHtmlTags(html);
  }
}
